import { marked } from "marked";

/**
 * Extracts content from within <document>...</document> tags.
 * If no tags are found, returns the original string as-is.
 */
export function extractDocumentContent(text: string): string {
  const match = text.match(/<document>([\s\S]*?)<\/document>/);
  return match ? match[1].trim() : text;
}

export function buildStyledHtml(markdown: string): string {
  const content = extractDocumentContent(markdown);
  const bodyHtml = marked.parse(content);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet">
  <style>
    *, *::before, *::after {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }

    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 11pt;
      line-height: 1.75;
      color: #2d2d2d;
      max-width: 100%;
      padding: 1.5em 2em;
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-rendering: optimizeLegibility;
      orphans: 3;
      widows: 3;
    }

    @page {
      size: A4;
    }

    /* Headings */
    h1, h2, h3, h4, h5, h6 {
      font-weight: 700;
      margin-top: 1.6em;
      margin-bottom: 0.5em;
      color: #1a1a1a;
      letter-spacing: -0.01em;
    }

    h1 {
      font-size: 1.8em;
      margin-top: 0;
      margin-bottom: 0.6em;
    }

    h1:first-child {
      border-bottom: 1px solid #d0d0d8;
      padding-bottom: 0.4em;
      margin-bottom: 0.8em;
    }

    h2 {
      font-size: 1.35em;
      margin-top: 1.8em;
    }

    h3 {
      font-size: 1.15em;
      font-weight: 600;
    }

    h4 {
      font-size: 1.0em;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: #555;
      font-size: 0.88em;
    }

    /* Paragraphs */
    p {
      margin-bottom: 0.85em;
    }

    /* Bold / emphasis */
    strong, b {
      color: #1a1a1a;
      font-weight: 600;
    }

    /* Links */
    a {
      color: #1a5276;
      text-decoration: none;
    }

    /* Lists */
    ul, ol {
      margin-bottom: 0.85em;
      padding-left: 1.6em;
    }

    li {
      margin-bottom: 0.35em;
      line-height: 1.65;
    }

    li > ul, li > ol {
      margin-top: 0.25em;
      margin-bottom: 0.25em;
    }

    /* Inline code */
    code {
      font-family: 'JetBrains Mono', 'Consolas', monospace;
      font-size: 0.87em;
      background-color: #f4f4f7;
      padding: 0.15em 0.4em;
      border-radius: 3px;
      color: #3d3d3d;
    }

    /* Code blocks */
    pre {
      background-color: #f7f7fa;
      color: #2d2d2d;
      padding: 1em 1.25em;
      border-radius: 6px;
      border: 1px solid #e5e5ea;
      overflow-x: auto;
      margin-bottom: 1.1em;
      page-break-inside: avoid;
    }

    pre code {
      background: none;
      padding: 0;
      color: inherit;
      font-size: 0.84em;
      line-height: 1.6;
      border: none;
    }

    /* Tables */
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 0.4em;
      margin-bottom: 1.2em;
      font-size: 0.92em;
      page-break-inside: avoid;
    }

    th, td {
      border: 1px solid #dcdce0;
      padding: 0.6em 0.9em;
      text-align: left;
      line-height: 1.5;
    }

    th {
      background-color: #37474f;
      color: #ffffff;
      font-weight: 600;
      font-size: 0.88em;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }

    tr:nth-child(even) {
      background-color: #f8f8fa;
    }

    /* Blockquotes */
    blockquote {
      border-left: 3px solid #b0b0b8;
      background-color: #fafafa;
      padding: 0.8em 1.2em;
      margin: 0.4em 0 1.1em 0;
      color: #4a4a4a;
    }

    blockquote p:last-child {
      margin-bottom: 0;
    }

    /* Horizontal rules */
    hr {
      border: none;
      border-top: 1px solid #e0e0e0;
      margin: 2em 0;
    }

    /* Images */
    img {
      max-width: 100%;
      height: auto;
    }

    /* Print helpers */
    h1, h2, h3, h4 {
      page-break-after: avoid;
    }

    table, pre, blockquote, img {
      page-break-inside: avoid;
    }

    li {
      page-break-inside: avoid;
    }
  </style>
</head>
<body>
${bodyHtml}
</body>
</html>`;
}
